import {
  Access,
  AccessClassification,
  accessesApi,
  AccountAccessData,
  CompanyAccessData,
} from '@/api/accesses';
import {
  accessesActions,
  accessesActions as actions,
} from '@/store/accesses/slice';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';

import { errorHandler } from '../errorHandler';
import { SagaPayload, SagaReturn } from '../types';
import { notifyActions } from '../notifications/slice';
import i18n from '@/i18n/i18n';
import { envs } from '@/shared/constants/envs';
import { AccessType } from '@/api/__generated__/webApi';
import { addClassificationAccess } from './utils/addClassificationAccess';
import { getTranslate } from '@/i18n/getTranslate.ts';

export function* accessesSaga() {
  yield all([
    takeLeading(actions.requestAccesses, requestAccesses),
    takeLeading(actions.requestAccessesRequests, requestAccessesRequests),
    takeLeading(actions.approveAccessesRequest, approveAccessesRequest),
    takeLeading(actions.declineAccessesRequest, declineAccessesRequest),
    takeEvery(actions.deleteAccessesRequest, deleteAccessesRequest),
    takeLeading(actions.deleteAccess, deleteAccessRequest),
  ]);
}

export function* requestAccesses(): SagaReturn {
  try {
    yield put(actions.setFetching({ accesses: true }));
    const accesses: Awaited<ReturnType<typeof accessesApi.getAccesses>> =
      yield call(accessesApi.getAccesses);
    yield put(
      actions.setAccesses({
        accesses: {
          outgoingAccountAccesses: addClassificationAccess(
            accesses.outgoingAccountAccesses,
            AccessClassification.Outgoing
          ) as AccountAccessData[],
          incomingAccountAccesses: addClassificationAccess(
            accesses.incomingAccountAccesses,
            AccessClassification.Incoming
          ) as AccountAccessData[],
          companyAccesses: addClassificationAccess(
            accesses.companyAccesses,
            AccessClassification.Incoming
          ) as CompanyAccessData[],
        },
      })
    );
  } catch (e) {
    errorHandler(e);
  } finally {
    yield put(actions.setFetching({ accesses: false }));
  }
}

export function* requestAccessesRequests(): SagaReturn {
  try {
    yield put(actions.setFetching({ accessesRequests: true }));
    const accessesRequests: Awaited<
      ReturnType<typeof accessesApi.getAccessesRequests>
    > = yield call(accessesApi.getAccessesRequests);
    yield put(actions.setAccessesRequests({ accessesRequests }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  } finally {
    yield put(actions.setFetching({ accessesRequests: false }));
  }
}

function* approveAccessesRequest({
  payload,
}: SagaPayload<typeof actions.approveAccessesRequest>): SagaReturn {
  try {
    yield call(accessesApi.approveAccessesRequest, payload);
    const relations: Awaited<ReturnType<typeof accessesApi.getAccesses>> =
      yield call(accessesApi.getAccesses);
    yield put(actions.setAccesses({ accesses: relations }));
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* declineAccessesRequest({
  payload,
}: SagaPayload<typeof actions.declineAccessesRequest>): SagaReturn {
  try {
    yield call(accessesApi.declineAccessesRequest, payload);
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* deleteAccessesRequest({
  payload,
}: SagaPayload<typeof actions.deleteAccessesRequest>): SagaReturn {
  try {
    yield call(accessesApi.deleteAccessesRequest, payload);
  } catch (e) {
    errorHandler(e, actions.setCommonError);
  }
}

function* deleteAccessRequest({
  payload,
}: SagaPayload<typeof actions.deleteAccess>): SagaReturn {
  const { accessId, type } = payload;
  const accesses: Access | null = yield select(
    (state) => state.accesses.accesses
  );
  try {
    yield call(accessesApi.deleteAccess, payload);

    if (type === AccessType.ACCOUNT) {
      const outgoingAccountAccesses =
        accesses?.outgoingAccountAccesses.filter(
          (access) => access.id !== accessId
        ) ?? [];
      const incomingAccountAccesses =
        accesses?.incomingAccountAccesses.filter(
          (access) => access.id !== accessId
        ) ?? [];
      yield put(
        accessesActions.setAccountAccesses({
          incomingAccountAccesses,
          outgoingAccountAccesses,
        })
      );
    } else {
      yield put(
        accessesActions.setCompanyAccess({
          companyAccesses:
            accesses?.companyAccesses.filter(
              (access) => access.id !== accessId
            ) ?? [],
        })
      );
    }
    const t: Awaited<ReturnType<typeof getTranslate<'accesses'>>> = yield call(
      getTranslate,
      'accesses'
    );
    yield put(
      notifyActions.showNotifications([
        {
          id: '',
          type: 'success',
          text: t('ACCESS_HAS_BEEN_SUCCESSFULLY_DELETED'),
        },
      ])
    );
  } catch (e) {
    errorHandler(e, actions.setCommonError);
    yield put(
      notifyActions.showNotifications([
        {
          id: '',
          type: 'error',
          text: i18n.t('SMTH_WENT_WRONG', {
            link: envs.PATIENT_CONTACT_SUPPORT_URL,
          }),
        },
      ])
    );
  }
}
